import React, { FC } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { SnapshotHooks, useFormat, useScreenSize, useSelectedCompany } from '../../../hooks/index';
import { EquityGraphData } from '../../../hooks/snapshot/index';
import { toRound } from '../../../utils/get-rounded-number.util';
import { toNumber } from '../../../utils/to-number.util';

interface CustomBarProps {
  fill: string;
  height: number;
  width: number;
  x: number;
  y: number;
}

export const CustomBar = (props: CustomBarProps) => {
  const { x, y, width, height, fill } = props;
  const radius = 4;

  const barPath = `
    M${x},${y + height}
    H${x + width}
    V${y + radius}
    Q${x + width},${y} ${x + width - radius},${y}
    H${x + radius}
    Q${x},${y} ${x},${y + radius}
    Z
  `;

  return <path d={barPath} fill={fill} />;
};

interface CustomTickProps {
  payload: { value: string };
  x: number;
  y: number;
}

const CustomXAxisTick: FC<CustomTickProps> = ({ x, y, payload }) => {
  const [date, name] = payload.value.split('|');
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={16}
        fill="#475467"
        fontSize={10}
        fontWeight={500}
        style={{ lineHeight: '16px', dominantBaseline: 'middle' }}
        textAnchor="middle"
        x={0}
        y={0}
      >
        {name}
      </text>
      <text
        dy={32}
        fill="#475467"
        fontSize={10}
        fontWeight={500}
        style={{ lineHeight: '16px', dominantBaseline: 'middle' }}
        textAnchor="middle"
        x={0}
        y={0}
      >
        {date}
      </text>
    </g>
  );
};

interface GraphData {
  date: string;
  estimatedPercentage: number;
  isCompanyIncorporation?: boolean;
  label: string;
  name: string;
  participation: number;
  parValue?: string;
  percentage: number;
  percentageChange: number;
  postMoneyValuation: number;
  totalConsideredPay?: number;
  totalIssuedShares?: number;
  totalRaised: number;
  value: number;
}

const CustomToolTip: FC<TooltipProps<number | string, keyof GraphData>> = ({ active, payload }) => {
  const { selectedCompany } = useSelectedCompany();
  const currency = selectedCompany?.currency;
  const currencyPrefix = currency?.symbol || currency?.iso3?.toUpperCase() || '';

  if (active && payload && payload.length) {
    const point = payload[0].payload as GraphData;
    const {
      date,
      postMoneyValuation,
      name,
      totalRaised,
      percentageChange,
      participation,
      estimatedPercentage,
      value,
      isCompanyIncorporation,
      parValue,
      totalIssuedShares,
      totalConsideredPay,
    } = point;

    if (isCompanyIncorporation) {
      return (
        <div className="flex w-[297px] flex-col gap-2 rounded-lg bg-gray-25 p-1 pt-2 shadow-2xl">
          <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
            <span className="text-label-sm font-semibold text-gray-700">
              <AppFormattedMessage id="COMPANY_INCORPORATION" />
            </span>
            <span className="text-label-sm font-semibold text-gray-700">{date}</span>
          </div>
          <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
            <span className="text-label-sm font-semibold text-gray-700">
              <AppFormattedMessage id="PAR_VALUE" />
            </span>
            <span className="text-label-sm font-semibold text-forest-600">
              {currencyPrefix}
              {parValue}
            </span>
          </div>
          <div className="flex justify-between gap-1 border-b border-dashed border-gray-200 px-4 pb-2">
            <span className="text-label-sm font-semibold text-gray-700">
              <AppFormattedMessage id="TOTAL_ISSUED_SHARES" />
            </span>
            <span className="text-label-sm font-semibold text-gray-700">
              {(totalIssuedShares ?? 0).toLocaleString('en-US')}
            </span>
          </div>
          <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
            <span className="text-label-sm font-semibold text-gray-700">
              <AppFormattedMessage id="TOTAL_CONSIDERED_PAY" />
            </span>
            <span className="text-label-sm font-semibold text-gray-700">
              {currencyPrefix}
              {(totalConsideredPay ?? 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
            <span className="text-label-sm font-semibold text-gray-700">
              <AppFormattedMessage id="YOUR_EQUITY" />
            </span>
            <span className="text-label-sm font-semibold text-gray-700">
              {estimatedPercentage}%
            </span>
          </div>
          <div className="flex justify-between gap-1 px-4 pb-2">
            <span className="text-label-sm font-semibold text-gray-700">
              <AppFormattedMessage id="YOUR_EQUITY_CHANGE" />
            </span>
            <span className="text-label-sm font-semibold text-gray-700">
              {percentageChange >= 0 ? `+${percentageChange}%` : `${percentageChange}%`}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex w-[297px] flex-col gap-2 rounded-lg bg-gray-25 p-1 pt-2 shadow-2xl">
        <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">{name}</span>
          <span className="text-label-sm font-semibold text-gray-700">{date}</span>
        </div>
        <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            <AppFormattedMessage id="POST_MONEY_VALUATION" />
          </span>
          <span className="text-label-sm font-semibold text-forest-600">
            {currencyPrefix}
            {postMoneyValuation.toLocaleString('en-US')}
          </span>
        </div>
        <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            {<AppFormattedMessage id="TOTAL_RAISED" />}
          </span>
          <span className="text-label-sm font-semibold text-gray-700">
            {currencyPrefix}
            {(totalRaised || 0).toLocaleString('en-US')}
          </span>
        </div>
        <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            <AppFormattedMessage id="YOUR_PARTICIPATION" />
          </span>
          <span className="text-label-sm font-semibold text-gray-700">
            {currencyPrefix}
            {(participation || 0).toLocaleString('en-US')}
          </span>
        </div>
        <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            <AppFormattedMessage id="VALUE_OF_SHARES" />
          </span>
          <span className="text-label-sm font-semibold text-gray-700">
            {currencyPrefix}
            {(value * 1000000).toLocaleString('en-US')}
          </span>
        </div>
        <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            <AppFormattedMessage id="YOUR_EQUITY" />
          </span>
          <span className="text-label-sm font-semibold text-gray-700">{estimatedPercentage}%</span>
        </div>
        <div className="flex justify-between gap-1 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            <AppFormattedMessage id="YOUR_EQUITY_CHANGE" />
          </span>
          <span className="text-label-sm font-semibold text-gray-700">
            {percentageChange >= 0 ? `+${percentageChange}%` : `${percentageChange}%`}
          </span>
        </div>
      </div>
    );
  }
};

export type EquityGraphProps = {
  companyId: string;
  stakeholderId: string;
};

const mockData: EquityGraphData[] = [
  {
    date: '2023-05-12',
    equity: 18.45,
    equityChange: 2.15,
    name: 'Alice',
    participation: 25.3,
    postMoneyValuation: 5600000,
    sharesValue: 45230,
    totalRaised: 1200000,
  },
  {
    date: '2022-11-03',
    equity: 12.78,
    equityChange: -1.32,
    name: 'Bob',
    participation: 18.9,
    postMoneyValuation: 4200000,
    sharesValue: 38750,
    totalRaised: 2500000,
  },
  {
    date: '2024-07-25',
    equity: 30.12,
    equityChange: 3.45,
    name: 'Charlie',
    participation: 40.0,
    postMoneyValuation: 8900000,
    sharesValue: 72500,
    totalRaised: 4500000,
  },
  {
    date: '2021-09-14',
    equity: 8.67,
    equityChange: -0.95,
    name: 'Diana',
    participation: 15.2,
    postMoneyValuation: 3100000,
    sharesValue: 21500,
    totalRaised: 900000,
  },
];

export const EquityGraph: FC<EquityGraphProps> = ({ companyId, stakeholderId }) => {
  const { format } = useFormat();
  const { width } = useScreenSize();
  const { graphData = [], isLoading } = SnapshotHooks.useEquityGraphData({
    companyId,
    stakeholderId,
  });

  const isGraphEmpty = toNumber(graphData?.length) <= 1;
  const sortedData = graphData.length ? graphData : mockData;

  const formattedGraphData = sortedData?.map(
    ({
      date,
      postMoneyValuation,
      totalRaised,
      name,
      equity,
      equityChange,
      participation,
      sharesValue,
      isCompanyIncorporation,
      parValue,
      totalIssuedShares,
      totalConsideredPay,
    }) => ({
      date: format(new Date(date), 'dd/MM/yyyy'),
      postMoneyValuation,
      totalRaised,
      name,
      value: (sharesValue || 0) / 1000000,
      estimatedPercentage: toRound(equity * 100, 2),
      percentageChange: toRound(equityChange * 100, 2),
      participation,
      isCompanyIncorporation,
      parValue,
      totalIssuedShares,
      totalConsideredPay,
      label: `${format(new Date(date), 'dd/MM/yyyy')}|${name}`,
    }),
  );

  return (
    <>
      <div
        className={twMerge(
          'flex min-h-[358px] flex-grow flex-col gap-3 overflow-hidden rounded-lg bg-gray-50 p-[2px] pt-3 lg:basis-[55%]',
          width < 660 && 'hidden',
        )}
      >
        <span className="pl-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id="EQUITY_AND_CAPITAL_CHANGE" />
        </span>

        <div className="centered relative flex h-full w-full gap-3 rounded-md bg-white px-5 py-6 shadow-sm">
          {!isGraphEmpty && (
            <div className="flex h-full max-w-[16px] items-center justify-center">
              <span className="-rotate-90 transform whitespace-nowrap text-label-sm font-[500] text-gray-500">
                <AppFormattedMessage id="YOUR_ESTIMATED_VALUE_MILLION" />
              </span>
            </div>
          )}

          {isGraphEmpty && !isLoading && (
            <span className="absolute text-nowrap text-label-md font-medium text-gray-500">
              <AppFormattedMessage id="TO_DISPLAY_CAPITAL_CHANGE_NEED_TO_HAVE_TWO_VALUATIONS" />
            </span>
          )}
          <div className="h-full w-full">
            <ResponsiveContainer
              className={twMerge(
                isGraphEmpty && 'pointer-events-none select-none opacity-30 blur-md',
              )}
              height="100%"
              width="100%"
            >
              <ComposedChart
                data={formattedGraphData}
                height={230}
                margin={{
                  top: 10,
                  right: 0,
                  bottom: 20,
                  left: 0,
                }}
                width={570}
              >
                <CartesianGrid horizontal={true} stroke="#F2F4F7" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={(props: CustomTickProps) => <CustomXAxisTick {...props} />}
                  tickFormatter={(value, index) => (index % 1 === 0 ? value : '')}
                  tickLine={false}
                />
                <YAxis
                  axisLine={false}
                  orientation="left"
                  stroke="#475467"
                  tick={{ fontSize: 10, fontWeight: 500 }}
                  tickLine={false}
                  tickMargin={8}
                  yAxisId="left"
                />
                <YAxis
                  axisLine={false}
                  domain={[0, 100]}
                  orientation="right"
                  stroke="#475467"
                  tick={{ fontSize: 10, fontWeight: 500 }}
                  tickFormatter={(value) => `${value}%`}
                  tickLine={false}
                  tickMargin={8}
                  yAxisId="right"
                />
                <Bar
                  dataKey="value"
                  fill="#2565C8"
                  shape={(props: unknown) => <CustomBar {...(props as CustomBarProps)} />}
                  yAxisId="left"
                />
                <Line
                  dataKey="estimatedPercentage"
                  dot={{ fill: '#DC6803', stroke: '#DC6803', r: 3 }}
                  stroke="#DC6803"
                  strokeDasharray="5 5"
                  type="linear"
                  yAxisId="right"
                />
                <Tooltip
                  content={(props: TooltipProps<number | string, keyof GraphData>) => (
                    <CustomToolTip {...props} />
                  )}
                  cursor={{ stroke: '#D0D5DD', strokeDasharray: '5 5' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {!isGraphEmpty && (
            <div className="flex h-full max-w-[16px] items-center justify-center">
              <span className="-rotate-90 transform whitespace-nowrap text-label-sm font-[500] text-gray-500">
                <AppFormattedMessage id="YOUR_EQUITY" /> (%)
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
